<template>
  <div class="canvas-wrapper">
    <loader v-if="isImgLoading" class="canvas-loader" />
    <canvas v-else id="canvas" ref="canvas" width="300" height="300" />
  </div>
</template>

<script>
import { fabric } from "fabric"
import { CANVAS_MODES } from '@/constants'
import Loader from '@/components/Loader'

const BASE_IMG = 'baseImg'
const LINE_POINTER = {
  end: 'pointer2',
  start: 'pointer1',
}
const MM_IN_INCH = 0.03937
const OBJECT_TYPES = {
  baseLine: 'baseLine',
  carBoxes: 'carBoxes',
  selectedLine: 'selectedLine',
  sizeLine: 'sizeLine',
  sizeText: 'sizeText',
}
const BASE_LINE_PARAMS = {
  strokeColor: 'orange',
  tag: OBJECT_TYPES.baseLine,
}

export default {
  name: 'CanvasWithSizeDrawing',
  components: {
    Loader,
  },
  props: {
    cars: {
      type: Array,
      default() {
        return []
      }
    },
    image: {
      type: Object,
      required: true,
    },
    isDrawBoxes: {
      type: Boolean,
      default: false
    },
    savedLines: {
      type: Array,
      default() {
        return []
      }
    },
    options: {
      type: Object,
      default() {
        return {
          mode: CANVAS_MODES.selecting,
          lineWidth: 3,
          lineColor: 'rgb(255, 0, 0)',
          sketchType: 'line'
        }
      }
    },
    wheelRadiusMm: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      baseLineRatio: 0,
      canvasCtx: null,
      isDown: false,
      isImgLoading: true,
      line: null,
    }
  },
  computed: {
    mode() {
      return this.options.mode
    },
    resolutionRatio() {
      if (this.image?.image_width > 4000) {
        return 1
      } else if (this.image?.image_width > 3000) {
        return 0.65
      } else {
        return 0.3
      }
    },
    wheelRadiusInch() {
      return this.wheelRadiusMm * MM_IN_INCH
    },
  },
  methods: {
    addLineSizeLabel(line = {}) {
      const length = this.getLineLength(line.width, line.height)
      const { left, top } = this.getUnitPosition(line)
      const text = new fabric.Text(this.getSizeWithUnit(length, this.baseLineRatio), {
        customData: {
          id: line?.customData?.id,
          type: OBJECT_TYPES.sizeText,
        },
        fontSize: 18 * this.resolutionRatio,
        fill: 'aqua',
        fontFamily: 'Open Sans',
        left,
        top,
      })
      this.canvasCtx.add(text)
      this.canvasCtx.renderAll()
    },
    calculeteBaseLineRatio() {
      const allObjects = this.canvasCtx.getObjects()
      if (!allObjects) {
        return
      }

      const lines = allObjects.reduce((res, cur) => {
        if (cur?.tag === OBJECT_TYPES.baseLine) {
          res.push(this.getLineLength(cur.width, cur.height))
        }
        return res
      }, [])

      if (!lines.length) {
        this.baseLineRatio = 0
        return
      }

      const averLength = (lines.reduce((res, cur) => res += cur, 0)) / lines.length
      this.baseLineRatio = this.wheelRadiusInch / averLength
    },
    calcLineXY(obj) {
      const centerPoint = obj?.getCenterPoint()
      const linePoint = obj?.calcLinePoints()
      const centerX = centerPoint?.x
      const centerY = centerPoint?.y
      return {
        x1: centerX + linePoint?.x1,
        y1: centerY + linePoint?.y1,
        x2: centerX + linePoint?.x2,
        y2: centerY + linePoint?.y2,
      }
    },
    changeObjectSelection(value) {
      this.canvasCtx.forEachObject(obj => {
        if (obj.type === 'image') {
          return
        }
        obj.selectable = value
      })
      this.canvasCtx.renderAll()
    },
    deleteLineForLabel(target) {
      this.canvasCtx.forEachObject(el => {
        if (el?.customData?.id && el?.customData?.id === target?.customData?.id) {
          this.canvasCtx.remove(el)
        }
      })
    },
    deleteLineSizeLabel(line) {
      this.canvasCtx.forEachObject(el => {
        if (el?.customData?.id && el?.customData?.id === line?.customData?.id) {
          this.canvasCtx.remove(el)
        }
      })
    },
    drawImages(images) {
      images.forEach(image => {
        const imgInstance = new fabric.Image(image.imageEl, {
          customData: {
            id: BASE_IMG,
          },
          left: 0,
          top: 0,
          width: image.imageEl.width,
          height: image.imageEl.height,
          originX: "left",
          originY: "top",
          selectable: false,
        })
        this.canvasCtx.add(imgInstance)
        const ratio = this.getCanvasHeight() / image.imageEl.height
        const xStart = (this.canvasCtx.width - image.imageEl.width * ratio) / 2
        this.canvasCtx.zoomToPoint(new fabric.Point(xStart, 0), ratio)
      })
    },
    drawLineInitial({ color, tag, way, width }) {
      const x1 = way[0].x
      const y1 = way[0].y
      const x2 = way[1].x
      const y2 = way[1].y

      const points = [x1, y1, x2, y2]
      const options = {
        hasControls: false,
        originX: 'center',
        originY: 'center',
        selectable: false,
        stroke: color,
        strokeWidth: width * this.resolutionRatio,
        tag,
      }
      if (tag === OBJECT_TYPES.sizeLine) {
        options.customData = this.getOptionsForLineWithSize()
      }
      const line = new fabric.Line(points, options)
      this.canvasCtx.add(line)
      if (tag === OBJECT_TYPES.sizeLine) {
        this.addLineSizeLabel(line)
      }
      this.canvasCtx.renderAll()
    },
    drawBaseLine() {
      this.drawLine(
        () => ({ ...BASE_LINE_PARAMS }),
        this.calculeteBaseLineRatio
      )
    },
    drawBoxes() {
      if (!this.cars.length) {
        return
      }

      const strokeColor = '#56e860'
      if(this.isDrawBoxes && this.cars[0].car_detections) {
        const baseImg = this.canvasCtx.getObjects().find(el => el?.customData?.id === BASE_IMG)
        const imgCoordsCornerTl =  baseImg?.aCoords?.tl
        this.cars.forEach(el => {
          const [x, y, w, h] = el?.car_detections?.bbox
          const left = imgCoordsCornerTl ? imgCoordsCornerTl?.x + x : x
          const top = imgCoordsCornerTl ? imgCoordsCornerTl?.y + y : y
          const rectangle = new fabric.Rect({
            customData: { type: OBJECT_TYPES.carBoxes },
            fill: '',
            height: h,
            left,
            stroke: strokeColor,
            selectable: false,
            strokeWidth: 2,
            top,
            width: w,
          })
          const text = new fabric.Text(this.getBoxlabel(el), {
            customData: { type: OBJECT_TYPES.carBoxes },
            fill: strokeColor,
            fontFamily: 'Open Sans',
            fontSize: 18,
            left,
            selectable: false,
            top: top - 20,
          })
          this.canvasCtx.add(rectangle)
          this.canvasCtx.add(text)
        })
      } else {
        this.canvasCtx.forEachObject(el => {
          if (el?.customData?.type === OBJECT_TYPES.carBoxes) {
            this.canvasCtx.remove(el)
          }
        })
      }
    },
    drawCirclesForLine(target) {
      target.set({
        label: OBJECT_TYPES.selectedLine,
        selectable: false,
      })
      const { x1, y1, x2, y2 } = this.calcLineXY(target)
      const circleOptions = {
        customData: { lineTag: target?.tag },
        fill: 'aqua',
        hasBorders: false,
        hasControls: false,
        opacity: 0.8,
        originX: 'center',
        originY: 'center',
        radius: target.strokeWidth * 2,
      }
      const pointerStart = new fabric.Circle({
        ...circleOptions,
        id: LINE_POINTER.start,
        left: x1,
        top: y1,
      })
      const pointerEnd = new fabric.Circle({
        ...circleOptions,
        id: LINE_POINTER.end,
        left: x2,
        top: y2,
      })
      this.canvasCtx.add(pointerStart, pointerEnd)
      this.canvasCtx.setActiveObject(pointerStart, pointerEnd)
      this.canvasCtx.renderAll()
    },
    drawLine(paramsFn = () => null, cb = () => null) {
      this.canvasCtx.on('mouse:down', ({ e }) => {
        this.isDown = true
        const { strokeColor, strokeWidth, ...props } = paramsFn() || {}
        const pointer = this.canvasCtx.getPointer(e)
        const points = [pointer.x, pointer.y, pointer.x, pointer.y]
        const lineOptions = {
          ...props,
          hasControls: false,
          originX: 'center',
          originY: 'center',
          selectable: false,
          stroke: strokeColor || this.options.lineColor,
          strokeWidth: (strokeWidth || this.options.lineWidth) * this.resolutionRatio,
        }
        this.line = new fabric.Line(points, lineOptions)
        this.canvasCtx.add(this.line)
      })
      this.canvasCtx.on('mouse:move', ({ e }) => {
        if (this.isDown) {
          const pointer = this.canvasCtx.getPointer(e)
          this.line.set({
            x2: pointer.x,
            y2: pointer.y
          })
          this.canvasCtx.renderAll()
        }
      })
      this.canvasCtx.on('mouse:up', () => {
        this.isDown = false
        cb(this.line)
      })
    },
    drawSketches() {
      this.savedLines.forEach(line => {
        switch (line.typeFigure) {
          case ('line'):
            this.drawLineInitial(line)
            break
          default:
        }
      })
    },
    enableMoving() {
      const group = new fabric.ActiveSelection(this.canvasCtx.getObjects(), {
        canvas: this.canvasCtx,
        hasBorders: false,
        hasControls: false,
        padding: 1000,
      })
      this.canvasCtx.setActiveObject(group)
    },
    enableSelection() {
      this.changeObjectSelection(true)
      this.canvasCtx.hoverCursor = 'move'
      this.canvasCtx.selection = true
    },
    getBoxlabel(tag) {
      const car_number = tag.car_number !== 'n/a' ? `${tag.car_number}` : ''
      const car_model = tag.car_model !== 'n/a' ? ` ${tag.car_model} ` : ''
      const car_orientation = tag.car_orientation !== 'n/a' ? ` ${tag.car_orientation}` : ''
      return car_number + car_orientation + car_model
    },
    getCanvasHeight() {
      return window.innerHeight - this.$refs.canvas.getBoundingClientRect().top
    },
    async getImage() {
      const imageEl = await this.loadImage(this.image.url)
      this.isImgLoading = false
      this.$nextTick(() => {
        this.init()
        this.drawImages([{ image: this.image, imageEl }])
        this.drawSketches()
        this.calculeteBaseLineRatio()
        this.updateCurrentMode(this.options.mode)
      })
    },
    getLineLength(a, b) {
      return Math.round(Math.sqrt(a*a + b*b))
    },
    getOptionsForLineWithSize() {
      return {
        id: `size-${new Date().valueOf()}-${Math.floor(Math.random() * 100000)}`,
        type: OBJECT_TYPES.sizeLine,
      }
    },
    getRelativeLineCoords(line, img) {
      const lineCoords = this.calcLineXY(line)
      if (!img) {
        return lineCoords
      }
      const imgCoordsCornerTl =  img?.aCoords?.tl || { x: 0, y: 0 }
      const x1 = lineCoords?.x1 - imgCoordsCornerTl?.x
      const y1 = lineCoords?.y1 - imgCoordsCornerTl?.y
      const x2 = lineCoords?.x2 - imgCoordsCornerTl?.x
      const y2 = lineCoords?.y2 - imgCoordsCornerTl?.y
      return { x1, y1, x2, y2 }
    },
    getSizeWithUnit(length = 0, ratio = 0) {
      return (length * ratio).toFixed(2) + 'in'
    },
    getUnitPosition(line) {
      if (!line) {
        return { left: 0, top: 0 }
      }
      const { x1, y1 } = this.calcLineXY(line)
      return { left: x1, top: y1 }
    },
    getUpdatingObjects() {
      const baseImg = this.canvasCtx.getObjects().find(el => el.customData?.id === BASE_IMG)
      const lines = this.canvasCtx.getObjects()
        .filter(el => el?.type === 'line' && el?.customData?.type !== OBJECT_TYPES.carBoxes)
        .map(el => {
          const { x1, y1, x2, y2 } = this.getRelativeLineCoords(el, baseImg)
          const res = {
            color: el.stroke,
            typeFigure: el.type,
            way: [{ x: x1, y: y1 }, { x: x2, y: y2 }],
            width: el.strokeWidth,
          }
          if (el.tag) {
            res.tag = el.tag
          }
          return res
        })
      return lines
    },
    mouseDblclick({ target }) {
      if (target?.type === 'line') {
        this.drawCirclesForLine(target)
      }
    },
    mouseWheel(opt) {
      let delta = opt.e.deltaY
      let zoom = this.canvasCtx.getZoom()
      zoom *= 0.999 ** delta
      if (zoom > 20) zoom = 20
      if (zoom < 0.01) zoom = 0.01
      this.canvasCtx.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom)
      opt.e.preventDefault()
      opt.e.stopPropagation()
    },
    init() {
      const canvas = new fabric.Canvas('canvas', {
        isDrawingMode: false,
        height: this.getCanvasHeight(),
        width: this.$refs.canvas.parentNode.offsetWidth,
      })
      canvas.on({
        'mouse:dblclick': this.mouseDblclick,
        'mouse:wheel': this.mouseWheel,
        'object:modified': this.objectModified,
        'object:moving': this.objectMoving,
        'object:removed': this.objectRemoved,
        'selection:created': this.selectionCreated,
        'selection:cleared': this.selectionCleared,
        'selection:updated': this.selectionUpdated,
      })
      this.canvasCtx = canvas
    },
    movingLineCircles(target) {
      if (target.id === LINE_POINTER.start) {
        this.udateSelectedLineCoords({ x1: target.left, y1: target.top })
      }
      if (target.id === LINE_POINTER.end) {
        this.udateSelectedLineCoords({ x2: target.left, y2: target.top })
      }
    },
    objectModified({ target }) {
      if (target.tag === OBJECT_TYPES.baseLine) {
        this.calculeteBaseLineRatio()
      }
      if (target?.customData?.type === OBJECT_TYPES.sizeLine) {
        this.updateLinesWithSize(this.baseLineRatio)
      }
    },
    objectMoving({ target }) {
      if (!target) {
        return
      }

      if (target.type === 'line') {
        target.set(this.calcLineXY(target))
        target.setCoords()
      }
      if (target.id === LINE_POINTER.start || target.id === LINE_POINTER.end) {
        this.movingLineCircles(target)
        if (target?.customData?.lineTag === OBJECT_TYPES.sizeLine) {
          this.updateLinesWithSize(this.baseLineRatio)
        }
        if (target?.customData?.lineTag === OBJECT_TYPES.baseLine) {
          this.calculeteBaseLineRatio()
        }
      }
    },
    objectRemoved({ target }) {
      if (target.tag === OBJECT_TYPES.baseLine) {
        this.calculeteBaseLineRatio()
      }
      if (target.tag === OBJECT_TYPES.sizeLine) {
        this.deleteLineSizeLabel(target)
      }
      if (target.customData.type === OBJECT_TYPES.sizeText) {
        this.deleteLineForLabel(target)
      }
    },
    selectionCleared() {
      fabric.Group.prototype.hasControls = true
      this.canvasCtx.forEachObject(el => {
        if (el.id === LINE_POINTER.start || el.id === LINE_POINTER.end) {
          this.canvasCtx.remove(el)
        }
        if (el.label === OBJECT_TYPES.selectedLine) {
          el.set({
            label: '',
            selectable: true,
          })
        }
      })
      this.canvasCtx.requestRenderAll()
    },
    selectionCreated() {
      fabric.Group.prototype.hasControls = false
    },
    selectionUpdated({ deselected, selected }) {
      if (selected[0].type === 'line' && deselected[0].type === 'circle') {
        this.selectionCleared()
      }
    },
    loadImage(url) {
      return new Promise(resolve => {
        const image = new Image()
        image.addEventListener('load', () => resolve(image))
        image.src = url
      })
    },
    removeActiveObjects() {
      this.canvasCtx.getActiveObjects().forEach(el => this.canvasCtx.remove(el))
    },
    removeDrawingEvents() {
      this.canvasCtx.off('mouse:down')
      this.canvasCtx.off('mouse:up')
      this.canvasCtx.off('mouse:move')
    },
    resetCanvasToDefaultSettings() {
      this.removeDrawingEvents()
      this.changeObjectSelection(false)
      this.canvasCtx.discardActiveObject()
      this.canvasCtx.isDrawingMode = false
      this.canvasCtx.hoverCursor = 'default'
      this.canvasCtx.selection = false
      this.canvasCtx.requestRenderAll()
    },
    updateLinesWithSize(newRatio) {
      const linesWithSize = this.canvasCtx.getObjects().reduce((res, cur) => {
        if (cur?.customData?.type === OBJECT_TYPES.sizeLine) {
          res[cur.customData.id] = cur
        }
        return res
      }, {})

      if (Object.keys(linesWithSize).length) {
        this.canvasCtx.forEachObject(obj => {
          if (obj?.customData?.type === OBJECT_TYPES.sizeText) {
            const line = linesWithSize[obj.customData.id]
            const { left, top } = this.getUnitPosition(line)
            const width = this.getLineLength(line.width, line.height)

            obj.text = this.getSizeWithUnit(width, newRatio)
            obj.left = left
            obj.top = top
          }
        })
        this.canvasCtx.renderAll()
      }
    },
    getLineSizeLabelParams() {
      return {
        customData: this.getOptionsForLineWithSize(),
        strokeColor: 'tomato',
        tag: OBJECT_TYPES.sizeLine,
      }
    },
    updateCurrentMode(curMode) {
      this.resetCanvasToDefaultSettings()
      if (curMode === CANVAS_MODES.calibration) {
        this.drawBaseLine()
      }
      if (curMode === CANVAS_MODES.drawing) {
        this.drawLine()
      }
      if (curMode === CANVAS_MODES.measuring) {
        this.drawLine(this.getLineSizeLabelParams, this.addLineSizeLabel)
      }
      if (curMode === CANVAS_MODES.moving) {
        this.enableMoving()
      }
      if (curMode === CANVAS_MODES.selecting) {
        this.enableSelection()
      }
    },
    udateSelectedLineCoords(coords) {
      this.canvasCtx.forEachObject(el => {
        if (el.label === OBJECT_TYPES.selectedLine) {
          el.set({
            ...this.calcLineXY(el),
            ...coords,
          })
          el.setCoords()
        }
      })
    },
    undoLine() {
      if (this.canvasCtx._objects.length > 0) {
        this.canvasCtx._objects.pop()
        this.canvasCtx.renderAll()
        this.calculeteBaseLineRatio()
      }
    },
  },
  watch: {
    baseLineRatio(v) {
      this.updateLinesWithSize(v)
    },
    isDrawBoxes() {
      if (this.mode === CANVAS_MODES.moving) {
        this.canvasCtx.discardActiveObject()
        this.drawBoxes()
        this.enableMoving()
      } else {
        this.drawBoxes()
      }
    },
    mode(v) {
      this.updateCurrentMode(v)
    },
  },
  mounted() {
    this.getImage()
  },
  beforeDestroy() {
    this.canvasCtx.off({
      'mouse:dblclick': this.mouseDblclick,
      'mouse:wheel': this.mouseWheel,
      'object:modified': this.objectModified,
      'object:moving': this.objectMoving,
      'object:removed': this.objectRemoved,
      'selection:created': this.selectionCreated,
      'selection:cleared': this.selectionCleared,
      'selection:updated': this.selectionUpdated,
    })
  },
}
</script>

<style lang="scss" scoped>
.canvas-wrapper {
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
}

.canvas-loader {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
}
</style>
